import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { RefreshCw, Filter, ChevronLeft, ChevronRight } from 'lucide-react'
import {
  getActivityLog,
  type AuditLogEvent,
  type ActivitySource,
  type AppSlot,
} from '../../lib/api'
import { actionFilterOptions } from '../../lib/activityActions'
import { useAutoRefresh } from '../../hooks/useAutoRefresh'
import ActivityTimeline from '../../components/ActivityTimeline'

type SourceFilter = '' | ActivitySource
type SlotFilter = '' | AppSlot

export default function Activity() {
  const navigate = useNavigate()
  const [events, setEvents] = useState<AuditLogEvent[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionFilter, setActionFilter] = useState<string>('')
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('')
  const [slotFilter, setSlotFilter] = useState<SlotFilter>('')
  const [page, setPage] = useState(0)
  const limit = 50

  const loadActivity = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getActivityLog({
        action: actionFilter || undefined,
        source: (sourceFilter || undefined) as ActivitySource | undefined,
        slot: (slotFilter || undefined) as AppSlot | undefined,
        limit,
        offset: page * limit,
      })
      setEvents(data.events)
      setTotal(data.total)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load activity')
      throw err
    } finally {
      setLoading(false)
    }
  }, [actionFilter, sourceFilter, slotFilter, page])

  useEffect(() => {
    loadActivity()
  }, [loadActivity])

  useAutoRefresh(loadActivity, {
    baseInterval: 30000,
    maxInterval: 300000,
  })

  const totalPages = Math.ceil(total / limit)
  const actionTypes = actionFilterOptions()

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold mb-2">Activity Log</h1>
          <p className="text-muted-foreground">
            System-wide audit trail and page-view history
          </p>
        </div>
        <button
          onClick={loadActivity}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-secondary transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-lg bg-destructive/10 text-destructive border border-destructive/20">
          {error}
        </div>
      )}

      <div className="mb-6 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <select
            value={actionFilter}
            onChange={(e) => {
              setActionFilter(e.target.value)
              setPage(0)
            }}
            className="px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {actionTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
        <select
          value={sourceFilter}
          onChange={(e) => {
            setSourceFilter(e.target.value as SourceFilter)
            setPage(0)
          }}
          className="px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="">All Sources</option>
          <option value="audit_log">Audit Log</option>
          <option value="platform_event">Page Visits</option>
        </select>
        <select
          value={slotFilter}
          onChange={(e) => {
            setSlotFilter(e.target.value as SlotFilter)
            setPage(0)
          }}
          className="px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="">All Slots</option>
          <option value="prod">Production</option>
          <option value="dev">Dev</option>
          <option value="branch">Branch</option>
        </select>
        <div className="text-sm text-muted-foreground">
          Showing {events.length} of {total} events
        </div>
      </div>

      <ActivityTimeline
        events={events}
        loading={loading}
        showUser
        onEventClick={(event) => {
          if (event.user_id) navigate(`/admin/users/${event.user_id}`)
        }}
      />

      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors disabled:opacity-50"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </button>
          <span className="text-sm text-muted-foreground">
            Page {page + 1} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors disabled:opacity-50"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  )
}
